import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { usePark } from "@/contexts/ParkContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader, formatCurrency } from "@/components/shared";
import { Sparkline } from "@/components/Sparkline";
import { TrendChip } from "@/components/TrendChip";
import {
  Building2, ParkingSquare, Users, Wallet, TrendingUp, CalendarRange,
  ArrowDownToLine, ArrowUpFromLine, FileWarning, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

function KpiCard({ icon: Icon, label, value, hint, testId, accent = "default" }) {
  const accents = {
    default: "bg-[#F0F0EC] text-[#284B3B]",
    green: "bg-[#E3F0E6] text-[#297343]",
    amber: "bg-[#FFF4D4] text-[#946A00]",
    red: "bg-[#F5E3E0] text-[#A13E34]",
  };
  return (
    <Card className="border-[#EAEAE5] shadow-none hover:shadow-sm transition-shadow" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#737A76] mb-2">{label}</div>
            <div className="text-3xl font-semibold tracking-tight text-[#1A211D] truncate" style={{ fontFamily: "Outfit" }}>{value}</div>
            {hint && <div className="text-xs text-[#737A76] mt-1">{hint}</div>}
          </div>
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${accents[accent]}`}>
            <Icon size={17} strokeWidth={1.8} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function OccupancyBar({ pct }) {
  const safe = Math.max(0, Math.min(100, pct || 0));
  const tone = safe >= 85 ? "bg-[#297343]" : safe >= 60 ? "bg-[#946A00]" : "bg-[#B5655C]";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 bg-[#F0F0EC] rounded-full overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${safe}%` }} />
      </div>
      <span className="text-xs font-medium text-[#1A211D] tabular-nums w-10 text-right">{safe}%</span>
    </div>
  );
}

export default function AgencyDashboardPage() {
  const navigate = useNavigate();
  const { switchPark } = usePark();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agency/dashboard");
      setData(data);
    } catch (e) {
      toast.error("Failed to load agency dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openParkDashboard = (parkId) => {
    switchPark(parkId);
    navigate("/dashboard");
  };

  const totals = data?.totals || {};
  const parks = data?.parks || [];

  return (
    <div data-testid="agency-dashboard-page">
      <PageHeader
        title="Agency Control Center"
        subtitle="Portfolio-wide KPIs across every park you operate."
        testId="agency-dashboard-header"
        actions={
          <Button variant="outline" onClick={load} data-testid="agency-refresh-btn">
            Refresh
          </Button>
        }
      />

      {/* Portfolio KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard
          icon={Building2}
          label="Parks in portfolio"
          value={totals.parks ?? 0}
          hint={`${totals.tenants ?? 0} tenants total`}
          testId="agency-kpi-parks"
        />
        <KpiCard
          icon={ParkingSquare}
          label="Portfolio occupancy"
          value={`${totals.occupancy_pct ?? 0}%`}
          hint={`${totals.occupied ?? 0} of ${totals.total_lots ?? 0} active lots`}
          testId="agency-kpi-occupancy"
          accent="green"
        />
        <KpiCard
          icon={Wallet}
          label="Outstanding A/R"
          value={formatCurrency(totals.outstanding_balance)}
          hint={`${totals.open_invoices ?? 0} open invoices`}
          testId="agency-kpi-outstanding"
          accent="red"
        />
        <KpiCard
          icon={TrendingUp}
          label="Revenue MTD"
          value={formatCurrency(totals.revenue_mtd)}
          hint={`${formatCurrency(totals.revenue_ytd)} YTD`}
          testId="agency-kpi-revenue"
          accent="green"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={ArrowDownToLine} label="Check-ins today" value={totals.check_ins_today ?? 0}
          hint={`${totals.check_ins_next_7 ?? 0} in next 7 days`} testId="agency-kpi-checkins" />
        <KpiCard icon={ArrowUpFromLine} label="Check-outs today" value={totals.check_outs_today ?? 0}
          hint={`${totals.check_outs_next_7 ?? 0} in next 7 days`} testId="agency-kpi-checkouts" />
        <KpiCard icon={CalendarRange} label="Active lots" value={totals.occupied ?? 0}
          hint="Currently checked-in" testId="agency-kpi-active" />
        <KpiCard icon={Users} label="Tenants" value={totals.tenants ?? 0}
          hint="Across all parks" testId="agency-kpi-tenants" />
      </div>

      {/* Portfolio 30-day revenue trend */}
      <Card className="border-[#EAEAE5] shadow-none mb-6" data-testid="agency-portfolio-trend-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#737A76] mb-1">
                Portfolio revenue — last 30 days
              </div>
              <div className="text-3xl font-semibold tracking-tight text-[#1A211D] flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
                {formatCurrency(totals.revenue_30d_total)}
                <TrendChip
                  direction={totals.trend_7d_direction}
                  pct={totals.trend_7d_pct}
                  size="sm"
                  testId="agency-portfolio-trend-chip"
                  title={`Last 7d ${formatCurrency(totals.revenue_last_7)} vs prior 7d ${formatCurrency(totals.revenue_prior_7)}`}
                />
              </div>
              <div className="text-xs text-[#737A76] mt-1">
                Daily payments received across all {totals.parks ?? 0} parks
              </div>
            </div>
            <div className="shrink-0">
              <Sparkline
                series={totals.revenue_30d || []}
                width={320}
                height={64}
                strokeWidth={1.8}
                stroke="#284B3B"
                fill="rgba(40,75,59,0.10)"
                tooltip
                testId="agency-portfolio-sparkline"
              />
              <div className="flex justify-between text-[10px] text-[#737A76] mt-1 tabular-nums">
                <span>30d ago</span><span>Today</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Per-park breakdown */}
      <Card className="border-[#EAEAE5] shadow-none" data-testid="agency-parks-table-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium" style={{ fontFamily: "Outfit" }}>
            Park breakdown
          </CardTitle>
          <span className="text-[10px] uppercase tracking-wider text-[#737A76]">{parks.length} parks</span>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="text-sm text-[#737A76] py-8 text-center">Loading…</div>
          ) : parks.length === 0 ? (
            <div className="text-sm text-[#737A76] py-8 text-center" data-testid="agency-empty-state">
              No parks yet. Create one from the Parks page.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm pd-table">
                <thead>
                  <tr className="text-left border-b border-[#EAEAE5] text-[10px] uppercase tracking-wider text-[#737A76]">
                    <th className="py-2 pr-3">Park</th>
                    <th className="px-3">Occupancy</th>
                    <th className="px-3 text-right">Tenants</th>
                    <th className="px-3 text-right">Today In/Out</th>
                    <th className="px-3 text-right">Open Inv</th>
                    <th className="px-3 text-right">Outstanding</th>
                    <th className="px-3 text-right">Revenue MTD</th>
                    <th className="px-3 text-right">Last 30d</th>
                    <th className="px-3 text-right">CRM</th>
                    <th className="pl-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {parks.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF7] cursor-pointer"
                      data-testid={`agency-park-row-${p.id}`}
                      onClick={() => openParkDashboard(p.id)}
                    >
                      <td className="py-3 pr-3">
                        <div className="font-medium text-[#1A211D]">{p.name}</div>
                        <div className="text-xs text-[#737A76]">
                          {[p.city, p.state].filter(Boolean).join(", ") || "—"}
                        </div>
                      </td>
                      <td className="px-3">
                        <OccupancyBar pct={p.occupancy_pct} />
                        <div className="text-[10px] text-[#737A76] mt-1 tabular-nums">
                          {p.active_reservations} / {p.total_lots} lots
                        </div>
                      </td>
                      <td className="px-3 text-right tabular-nums">{p.tenants}</td>
                      <td className="px-3 text-right tabular-nums">
                        <span className="text-[#297343]">{p.check_ins_today}</span>
                        <span className="text-[#737A76]"> / </span>
                        <span className="text-[#B5655C]">{p.check_outs_today}</span>
                      </td>
                      <td className="px-3 text-right tabular-nums">
                        {p.open_invoices > 0 ? (
                          <span className="inline-flex items-center gap-1 text-[#946A00]">
                            <FileWarning size={12} strokeWidth={2} />{p.open_invoices}
                          </span>
                        ) : (
                          <span className="text-[#737A76]">0</span>
                        )}
                      </td>
                      <td className={`px-3 text-right tabular-nums ${p.outstanding_balance > 0 ? "text-[#A13E34] font-medium" : "text-[#737A76]"}`}>
                        {formatCurrency(p.outstanding_balance)}
                      </td>
                      <td className="px-3 text-right tabular-nums font-medium">{formatCurrency(p.revenue_mtd)}</td>
                      <td className="px-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-col items-end gap-0.5">
                          <Sparkline
                            series={p.revenue_30d || []}
                            width={110}
                            height={26}
                            stroke="#284B3B"
                            fill="rgba(40,75,59,0.10)"
                            tooltip
                            testId={`agency-park-sparkline-${p.id}`}
                          />
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-[#737A76] tabular-nums">{formatCurrency(p.revenue_30d_total)}</span>
                            <TrendChip
                              direction={p.trend_7d_direction}
                              pct={p.trend_7d_pct}
                              testId={`agency-park-trend-${p.id}`}
                              title={`Last 7d ${formatCurrency(p.revenue_last_7)} vs prior 7d ${formatCurrency(p.revenue_prior_7)}`}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-3 text-right">
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${p.ghl_enabled ? "bg-[#297343]" : "bg-[#D7D7D2]"}`}
                          title={p.ghl_enabled ? "FreedomCRM connected" : "FreedomCRM not configured"}
                        />
                      </td>
                      <td className="pl-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          data-testid={`agency-open-park-${p.id}`}
                          onClick={(e) => { e.stopPropagation(); openParkDashboard(p.id); }}
                        >
                          Open <ExternalLink size={12} className="ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
